import { useRef, useState, type MouseEvent } from 'react';
import { ddmmyy } from '@mds/shared/lib/format';

export interface TrendPoint {
  /** yyyy-MM for a month; yyyy-MM-dd for a day or the Monday of a week. */
  key: string;
  value: number;
  /** Marks a bucket that has not finished yet, so the dip is not read as a fall. */
  partial?: boolean;
}

/** How wide each point is. Decides the axis labels and the caption wording. */
export type TrendGranularity = 'day' | 'week' | 'month';

/** Fractions of the axis top that get a gridline and a tick label. */
const TICK_STOPS = [0, 0.25, 0.5, 0.75, 1];

/** Past this many points, thin the axis labels so they cannot collide. */
const LABEL_THINNING_ABOVE = 8;

/** The most axis labels that fit across the plot at the label font size. */
const MAX_AXIS_LABELS = 12;

/** Above this, ticks read as "$12.5k" rather than "$12,500". */
const THOUSANDS_FORMAT_ABOVE = 10000;

const W = 760;
const H = 300;
const PAD = { top: 18, right: 22, bottom: 46, left: 66 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

/**
 * Round a maximum up to a clean axis top. The ladder is deliberately fine: a
 * coarse 1/2/5 ladder sent a max of 6,237 to a top of 10,000, leaving the plot
 * filling 62% of its height and flattening the line.
 */
function axisTop(max: number) {
  if (max <= 0) return 1;
  const mag = 10 ** Math.floor(Math.log10(max));
  for (const step of [1, 1.2, 1.5, 2, 2.5, 3, 4, 5, 6, 8, 10]) {
    if (max <= step * mag) return step * mag;
  }
  return 10 * mag;
}

/**
 * One formatter for every tick on the axis, chosen from the axis top — so the
 * scale never mixes styles the way "$7,500 | $10.0k" does.
 */
function tickFormatter(top: number) {
  return top >= THOUSANDS_FORMAT_ABOVE
    ? (n: number) => `$${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`
    : (n: number) => `$${Math.round(n).toLocaleString()}`;
}

const dateOf = (key: string) => {
  const [y, m, d] = key.split('-').map(Number);
  return new Date(y, m - 1, d ?? 1);
};

/** The axis label for one point: "Sep", "08/09" or "w/c 08/09". */
const axisLabel = (key: string, granularity: TrendGranularity) => {
  const d = dateOf(key);
  if (granularity === 'month') return d.toLocaleDateString(undefined, { month: 'short' });
  // Ticks drop the year to stay narrow; the caption carries it.
  const day = ddmmyy(d).slice(0, 5);
  return granularity === 'week' ? `w/c ${day}` : day;
};

/** The caption wording for one point: "in Sep 2026", "on 08/09/26", "in the week of 08/09/26". */
const captionLabel = (key: string, granularity: TrendGranularity) => {
  const d = dateOf(key);
  if (granularity === 'month') {
    return `in ${d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`;
  }
  const day = ddmmyy(d);
  return granularity === 'week' ? `in the week of ${day}` : `on ${day}`;
};

const PARTIAL_NOTE: Record<TrendGranularity, string> = {
  day: ' · today so far',
  week: ' · week to date',
  month: ' · month to date',
};

/**
 * Single-series revenue trend.
 *
 * One series, so no legend — the card title says what is plotted. A 2px line
 * over a 10%-opacity wash of the same hue, hairline solid gridlines one step
 * off the surface, and a crosshair that reports the value under the cursor.
 */
export default function TrendLine({
  points,
  format,
  granularity = 'month',
  title = 'Revenue',
}: {
  points: TrendPoint[];
  format: (n: number) => string;
  granularity?: TrendGranularity;
  /** What the series measures, for the chart's accessible name. */
  title?: string;
}) {
  const [hover, setHover] = useState<number | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);

  if (points.length < 2) {
    return <p className="m-0 text-base text-ink-muted">Not enough history to plot a trend.</p>;
  }

  const top = axisTop(Math.max(...points.map((p) => p.value)));
  const tick = tickFormatter(top);
  const x = (i: number) => PAD.left + (i / (points.length - 1)) * PLOT_W;
  const y = (v: number) => PAD.top + PLOT_H - (v / top) * PLOT_H;

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i)} ${y(p.value)}`).join(' ');
  const area =
    `M${x(0)} ${PAD.top + PLOT_H} ` +
    points.map((p, i) => `L${x(i)} ${y(p.value)}`).join(' ') +
    ` L${x(points.length - 1)} ${PAD.top + PLOT_H} Z`;

  const ticks = TICK_STOPS.map((f) => f * top);
  const last = points.length - 1;
  const activeIndex = hover ?? last;
  const active = points[activeIndex];

  const track = (event: MouseEvent<SVGRectElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const px = ((event.clientX - rect.left) / rect.width) * W;
    const i = Math.round(((px - PAD.left) / PLOT_W) * (points.length - 1));
    setHover(Math.max(0, Math.min(points.length - 1, i)));
  };

  return (
    <figure className="m-0">
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        className="block h-auto w-full"
        role="img"
        aria-label={`${title} by ${granularity}, ${points[0].key} to ${points[last].key}`}
      >
        {/* Recessive hairline grid — solid, never dashed. */}
        {ticks.map((t) => (
          <g key={t}>
            <line
              x1={PAD.left}
              x2={W - PAD.right}
              y1={y(t)}
              y2={y(t)}
              stroke="var(--chart-grid)"
              strokeWidth="1"
            />
            <text
              x={PAD.left - 12}
              y={y(t) + 5}
              textAnchor="end"
              fontSize="15"
              fill="var(--ink-muted)"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {tick(t)}
            </text>
          </g>
        ))}

        <path d={area} fill="var(--chart-mark)" opacity="0.1" />
        <path
          d={line}
          fill="none"
          stroke="var(--chart-mark)"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeLinecap="round"
        />

        {/* A partial final bucket is drawn hollow so the dip reads as incomplete. */}
        {points.map((p, i) =>
          i === last || i === activeIndex ? (
            <circle
              key={p.key}
              cx={x(i)}
              cy={y(p.value)}
              r="5"
              fill={p.partial ? 'var(--surface)' : 'var(--chart-mark)'}
              stroke={p.partial ? 'var(--chart-mark)' : 'var(--surface)'}
              strokeWidth="2"
            />
          ) : null,
        )}

        {hover !== null && (
          <line
            x1={x(hover)}
            x2={x(hover)}
            y1={PAD.top}
            y2={PAD.top + PLOT_H}
            stroke="var(--chart-mark)"
            strokeWidth="1"
            opacity="0.55"
          />
        )}

        {points.map((p, i) => {
          // Thin out labels so they never collide: every other month on a
          // 12-point axis, every nth day on a 31-point one.
          const every =
            points.length > LABEL_THINNING_ABOVE
              ? Math.max(2, Math.ceil(points.length / MAX_AXIS_LABELS))
              : 1;
          if (i % every !== 0 && i !== last) return null;
          return (
            <text
              key={p.key}
              x={x(i)}
              y={H - 18}
              textAnchor="middle"
              fontSize="15"
              fill="var(--ink-muted)"
            >
              {axisLabel(p.key, granularity)}
            </text>
          );
        })}

        <rect
          x={PAD.left}
          y={PAD.top}
          width={PLOT_W}
          height={PLOT_H}
          fill="transparent"
          onMouseMove={track}
          onMouseLeave={() => setHover(null)}
        />
      </svg>

      <figcaption className="mt-2 text-base text-ink-secondary">
        <span className="font-semibold tabular-nums">{format(active.value)}</span>{' '}
        <span className="text-ink-muted">
          {captionLabel(active.key, granularity)}
          {active.partial ? PARTIAL_NOTE[granularity] : ''}
        </span>
      </figcaption>
    </figure>
  );
}
